import React from 'react';
import StaticContentPage from '../components/StaticContentPage';
import { Mail, MessageCircle, Heart, LifeBuoy, ShieldCheck, Users, Info, Scale } from 'lucide-react';

const ContactCard = ({ icon: Icon, title, email, children }) => (
  <div className="p-8 rounded-3xl border border-gray-100 hover:border-brand-indigo/30 transition-all active:scale-[0.99] group bg-white shadow-sm hover:shadow-md">
    <div className="flex items-center gap-4 mb-6">
      <div className="p-4 rounded-2xl bg-gray-50 text-gray-400 group-hover:bg-brand-indigo/10 group-hover:text-brand-indigo transition-colors">
        <Icon size={24} />
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        <a href={`mailto:${email}`} className="text-brand-indigo font-bold hover:underline">{email}</a>
      </div>
    </div>
    <div className="text-gray-600 leading-relaxed space-y-2">
      {children}
    </div>
  </div>
);

const Contact = () => {
  return (
    <StaticContentPage title="Contact Us">
      <div className="max-w-3xl mx-auto text-center mb-16">
        <p className="text-xl font-medium text-gray-700 leading-relaxed mb-6">
          Thank you for your interest in Chooziq.
        </p>
        <p className="text-gray-600 leading-relaxed">
          We are always open to hearing from our users, communities, organizations, educators, event hosts, and collaborators. Whether you have a question, feedback, partnership inquiry, or need support, our team is here to help.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
        <ContactCard icon={MessageCircle} title="General Inquiries" email="contact@chooziq.com">
          <p>For general questions about Chooziq, the platform, communities, or features.</p>
        </ContactCard>

        <ContactCard icon={LifeBuoy} title="Support & Assistance" email="support@chooziq.com">
          <p>If you are experiencing technical issues or need help using the platform, please include:</p>
          <ul className="text-sm space-y-1 mt-2">
            <li className="flex items-center gap-2">• a description of the issue</li>
            <li className="flex items-center gap-2">• screenshots if applicable</li>
            <li className="flex items-center gap-2">• device or browser information</li>
            <li className="flex items-center gap-2">• relevant session or community details</li>
          </ul>
        </ContactCard>

        <ContactCard icon={ShieldCheck} title="Privacy & Data" email="privacy@chooziq.com">
          <p>For questions related to privacy, personal data, or account information.</p>
        </ContactCard>

        <ContactCard icon={Scale} title="Legal & Policy" email="legal@chooziq.com">
          <p>For legal concerns, policy inquiries, copyright matters, or compliance requests.</p>
        </ContactCard>

        <ContactCard icon={Users} title="Partnerships" email="partnerships@chooziq.com">
          <p>We welcome collaboration with educational institutions, organizations, event organizers, and tech partners.</p>
        </ContactCard>

        <ContactCard icon={Heart} title="Feedback" email="feedback@chooziq.com">
          <p>If you have ideas, feature suggestions, or recommendations, we would love to hear from you.</p>
        </ContactCard>
      </div>

      <div className="bg-gray-50 p-10 rounded-3xl border border-gray-100 mb-16">
        <div className="flex flex-col md:flex-row gap-8 items-center">
          <div className="p-4 rounded-2xl bg-white shadow-sm">
            <Info className="text-brand-indigo" size={32} />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Response Time</h3>
            <p className="text-gray-600 leading-relaxed">
              We aim to respond to inquiries as quickly as possible. Response times may vary depending on the nature and volume of requests.
            </p>
          </div>
        </div>
      </div>

      <div className="text-center mb-16">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Stay Connected</h3>
        <p className="text-gray-600 mb-8">To learn more about Chooziq and upcoming platform updates, visit:</p>
        <div className="flex justify-center gap-8">
          {['Twitter', 'LinkedIn', 'Discord'].map(social => (
            <a key={social} href="#" className="font-bold text-brand-indigo hover:text-brand-indigo/80 transition-colors">{social}</a>
          ))}
        </div>
        <p className="mt-8 font-bold text-gray-900">Website: Chooziq</p>
      </div>

      <div className="p-10 bg-brand-indigo rounded-3xl text-white text-center">
        <h3 className="text-2xl font-bold mb-6">Our Commitment</h3>
        <p className="max-w-2xl mx-auto leading-relaxed opacity-90">
          At Chooziq, we are committed to building a platform centered around realtime engagement, collaboration, participation, and strong community experiences. Your feedback helps us continue improving the platform for everyone.
        </p>
      </div>
    </StaticContentPage>
  );
};

export default Contact;
